import * as React from 'react';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

export const chipVariants = cva(
  'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors',
  {
    variants: {
      variant: {
        default:
          'bg-surface-elevated text-text-secondary border border-border',
        primary:
          'bg-primary-muted text-primary border border-primary/15',
        secondary:
          'bg-secondary-muted text-secondary border border-secondary/15',
        accent:
          'bg-accent-muted text-accent border border-accent/15',
        success:
          'bg-success-bg text-success-text border border-success-border',
        warning:
          'bg-warning-bg text-warning-text border border-warning-border',
        danger: 'bg-danger-bg text-danger-text border border-danger-border',
        info: 'bg-info-bg text-info-text border border-info-border',
        outline: 'border border-border bg-transparent text-text-secondary',
      },
      size: {
        default: 'h-7 px-2.5',
        sm: 'h-6 px-2 text-[11px]',
        lg: 'h-8 px-3 text-sm',
      },
      interactive: {
        true: 'cursor-pointer hover:bg-surface-hover',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      interactive: false,
    },
  }
);

export interface ChipProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof chipVariants> {
  onRemove?: () => void;
}

export const Chip = React.forwardRef<HTMLSpanElement, ChipProps>(
  ({ className, variant, size, interactive, onRemove, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(chipVariants({ variant, size, interactive }), className)}
        {...props}
      >
        {children}
        {onRemove && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onRemove();
            }}
            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-current opacity-70 hover:bg-black/10 hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-primary"
            aria-label="Eliminar"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    );
  }
);
Chip.displayName = 'Chip';
